import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { logout } from '../../services/auth';

function Navbar({ user }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBillingDropdownOpen, setIsBillingDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const billingRef = useRef(null);
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileBillingRef = useRef(null);
  const mobileMenuButtonRef = useRef(null);

  // Handle billing dropdown toggle - close user menu if open (desktop only)
  const handleBillingToggle = () => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
      // Wait for exit animation before toggling billing dropdown
      setTimeout(() => {
        setIsBillingDropdownOpen(prev => !prev);
      }, 150);
    } else {
      setIsBillingDropdownOpen(prev => !prev);
    }
  };

  // Handle mobile billing dropdown toggle - don't close main mobile menu
  const handleMobileBillingToggle = () => {
    setIsBillingDropdownOpen(prev => !prev);
  };

  // Handle user menu toggle - close billing dropdown if open
  const handleUserMenuToggle = () => {
    if (isBillingDropdownOpen) {
      setIsBillingDropdownOpen(false);
      // Wait for exit animation before toggling user menu
      setTimeout(() => {
        setIsMenuOpen(prev => !prev);
      }, 150);
    } else {
      setIsMenuOpen(prev => !prev);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check all possible containers (including mobile menu button)
      const clickedInsideBilling = billingRef.current?.contains(event.target);
      const clickedInsideUserMenu = userMenuRef.current?.contains(event.target);
      const clickedInsideMobileMenu = mobileMenuRef.current?.contains(event.target);
      const clickedInsideMobileBilling = mobileBillingRef.current?.contains(event.target);
      const clickedInsideMobileButton = mobileMenuButtonRef.current?.contains(event.target);
      
      // Check if click is inside any dropdown container or mobile menu button
      const clickedInsideAnyDropdown = 
        clickedInsideBilling || 
        clickedInsideUserMenu || 
        clickedInsideMobileMenu || 
        clickedInsideMobileBilling ||
        clickedInsideMobileButton;
      
      // If click is outside all containers, close all dropdowns
      if (!clickedInsideAnyDropdown) {
        setIsBillingDropdownOpen(false);
        setIsMenuOpen(false);
      }
    };

    if (isBillingDropdownOpen || isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isBillingDropdownOpen, isMenuOpen]);

  const handleLogout = async () => {
    // Show confirmation dialog
    if (!window.confirm('Are you sure you want to logout?')) {
      return;
    }

    try {
      await logout();
      // Force a page reload to clear the app state and redirect to login
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed:', error);
      // Even on error, clear local auth and redirect
      window.location.href = '/login';
    }
  };

  return (
    <nav className="bg-blue-600 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/dashboard" className="flex items-center">
              <img 
                src="/assets/images/logo.svg" 
                alt="Member Hub Logo"
                className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 mr-3"
              />
              <span className={`text-lg sm:text-xl font-semibold ${window.location.hostname === 'members-staging.research-triangle.ai' ? 'text-red-400' : ''}`}>Member Hub</span>
            </Link>
          </div>
          
          {/* Desktop navigation */}
          <div className="hidden md:flex items-center space-x-4">
            <Link to="/dashboard" className="px-3 py-2 rounded-md hover:bg-blue-700">
              Dashboard
            </Link>
            <Link to="/events" className="px-3 py-2 rounded-md hover:bg-blue-700">
              Events
            </Link>
            <div className="relative" ref={billingRef}>
              <button
                className="flex items-center px-3 py-2 rounded-md hover:bg-blue-700"
                onClick={handleBillingToggle}
              >
                <span className="mr-1">Billing</span>
                <svg
                  className={`h-4 w-4 transform transition-transform duration-200 ${isBillingDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <AnimatePresence>
                {isBillingDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                    className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-20"
                  >
                    <Link
                      to="/billing-settings"
                      className="block px-4 py-2 text-gray-800 hover:bg-gray-100"
                      onClick={() => setIsBillingDropdownOpen(false)}
                    >
                      Billing Settings
                    </Link>
                    <Link
                      to="/billing-history"
                      className="block px-4 py-2 text-gray-800 hover:bg-gray-100"
                      onClick={() => setIsBillingDropdownOpen(false)}
                    >
                      Billing History
                    </Link>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div className="relative" ref={userMenuRef}>
              <button
                className="flex items-center px-3 py-2 rounded-md hover:bg-blue-700"
                onClick={handleUserMenuToggle}
              >
                <span className="mr-2">{user?.first_name || user?.email}</span>
                <svg
                  className={`h-5 w-5 transform transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <AnimatePresence>
                {isMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                    className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-20"
                  >
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-gray-800 hover:bg-gray-100"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Profile
                    </Link>
                    <Link
                      to="/support"
                      className="block px-4 py-2 text-gray-800 hover:bg-gray-100"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Support
                    </Link>
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        handleLogout();
                      }}
                      className="block w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-100"
                    >
                      Logout
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
          
          {/* Mobile menu button */}
          <div className="md:hidden flex items-center" ref={mobileMenuButtonRef}>
            <button
              onClick={() => {
                // Close billing dropdown when toggling mobile menu
                if (isMenuOpen) {
                  setIsBillingDropdownOpen(false);
                }
                setIsMenuOpen(!isMenuOpen);
              }}
              className="inline-flex items-center justify-center p-2 rounded-md hover:bg-blue-700"
            >
              <svg
                className={`h-6 w-6 ${isMenuOpen ? 'hidden' : 'block'}`}
                stroke="currentColor"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <svg
                className={`h-6 w-6 ${isMenuOpen ? 'block' : 'hidden'}`}
                stroke="currentColor"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile menu */}
      <div className={`md:hidden ${isMenuOpen ? 'block' : 'hidden'}`} ref={mobileMenuRef}>
        <div className="px-2 pt-2 pb-3 space-y-1">
          <Link
            to="/dashboard"
            className="block px-3 py-2 rounded-md text-base font-medium hover:bg-blue-700"
            onClick={() => setIsMenuOpen(false)}
          >
            Dashboard
          </Link>
          <Link
            to="/events"
            className="block px-3 py-2 rounded-md text-base font-medium hover:bg-blue-700"
            onClick={() => setIsMenuOpen(false)}
          >
            Events
          </Link>
          <div ref={mobileBillingRef}>
            <button
              className="flex items-center justify-between w-full px-3 py-2 rounded-md text-base font-medium hover:bg-blue-700"
              onClick={handleMobileBillingToggle}
            >
              <span>Billing</span>
              <svg
                className={`h-4 w-4 transform transition-transform duration-200 ${isBillingDropdownOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isBillingDropdownOpen && (
              <div className="ml-4 mt-1 space-y-1">
                <Link
                  to="/billing-settings"
                  className="block px-3 py-2 rounded-md text-sm hover:bg-blue-700"
                  onClick={() => {
                    setIsMenuOpen(false);
                    setIsBillingDropdownOpen(false);
                  }}
                >
                  Billing Settings
                </Link>
                <Link
                  to="/billing-history"
                  className="block px-3 py-2 rounded-md text-sm hover:bg-blue-700"
                  onClick={() => {
                    setIsMenuOpen(false);
                    setIsBillingDropdownOpen(false);
                  }}
                >
                  Billing History
                </Link>
              </div>
            )}
          </div>
          <Link
            to="/profile"
            className="block px-3 py-2 rounded-md text-base font-medium hover:bg-blue-700"
            onClick={() => setIsMenuOpen(false)}
          >
            Profile
          </Link>
          <Link
            to="/support"
            className="block px-3 py-2 rounded-md text-base font-medium hover:bg-blue-700"
            onClick={() => setIsMenuOpen(false)}
          >
            Support
          </Link>
          <button
            onClick={() => {
              setIsMenuOpen(false);
              handleLogout();
            }}
            className="block w-full text-left px-3 py-2 rounded-md text-base font-medium hover:bg-blue-700"
          >
            Logout
          </button>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;